import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Button,
  CircularProgress,
} from '@mui/material';
import { School as SchoolIcon, Quiz as QuizIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../api/axios';
import Summary from './Summary';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import AddExerciseDialog from './AddExerciseDialog';
import EditConceptBlockDialog from './EditConceptBlockDialog';
import ConceptBlock from './ConceptBlock';
import IntroductionEditor from './IntroductionEditor';

const Section = ({ section, title, order = null, hideTitle = false, sectionTitle = null, notebookId = null, isTeacher = false, onSectionUpdate = null, onSectionDelete = null }) => {
  const navigate = useNavigate();
  const { notebookId: paramNotebookId } = useParams();
  const currentNotebookId = notebookId || paramNotebookId;
  const [loadingFirstExercise, setLoadingFirstExercise] = useState(false);
  const [addExerciseDialogOpen, setAddExerciseDialogOpen] = useState(false);
  const [addingConceptBlock, setAddingConceptBlock] = useState(false);
  const [deletingConceptBlock, setDeletingConceptBlock] = useState({}); // { blockIndex: true/false }
  const [editingConceptBlock, setEditingConceptBlock] = useState(null); // blockIndex or null
  const [editingConceptBlockData, setEditingConceptBlockData] = useState(null); // { definition, notes, theorems }
  const [savingConceptBlock, setSavingConceptBlock] = useState(false);
  
  if (!section) {
    return null;
  }

  const { introduction, summary, concept_blocks, standalone_notes, exercises } = section;

  // Handle clicking "查看练习" button - navigate to first exercise in this section
  const handleViewExercises = async () => {
    if (!currentNotebookId || !sectionTitle) {
      return;
    }

    try {
      setLoadingFirstExercise(true);
      const response = await axios.get(`/notebook-examples/notebook/${currentNotebookId}`);
      const allExamples = response.data.examples || [];
      
      // Filter examples from this section
      const sectionExamples = allExamples.filter(ex => ex.sectionTitle === sectionTitle);
      
      // Navigate to first exercise if exists
      if (sectionExamples.length > 0) {
        const firstExampleId = sectionExamples[0].id;
        navigate(`/notebook-exercise/${firstExampleId}`);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoadingFirstExercise(false);
    }
  };

  // Handle editing concept block
  const handleEditConceptBlock = (blockIndex, block) => {
    // 如果传入了完整的 block，直接使用；否则从 concept_blocks 中获取
    const targetBlock = block || (concept_blocks && concept_blocks[blockIndex]);
    if (!targetBlock) {
      return;
    }
    setEditingConceptBlock(blockIndex);
    setEditingConceptBlockData({
      definition: targetBlock.definition || '',
      notes: targetBlock.notes || [],
      theorems: targetBlock.theorems || [],
      examples: targetBlock.examples || []
    });
  };

  // Handle deleting concept block
  const handleDeleteConceptBlock = async (blockIndex) => {
    if (!currentNotebookId || !sectionTitle) {
      return;
    }
    
    if (!window.confirm('确定要删除这个概念块吗？此操作不可撤销。')) {
      return;
    }
    
    try {
      setDeletingConceptBlock(prev => ({ ...prev, [blockIndex]: true }));
      await axios.delete(`/notes/${currentNotebookId}/section/${encodeURIComponent(sectionTitle)}/concept-block/${blockIndex}`);
      if (onSectionUpdate) {
        onSectionUpdate();
      }
    } catch (error) {
      console.error('Error deleting concept block:', error);
      alert('删除失败：' + (error.response?.data?.error || error.message));
    } finally {
      setDeletingConceptBlock(prev => ({ ...prev, [blockIndex]: false }));
    }
  };

  // Handle saving concept block
  const handleSaveConceptBlock = async (conceptBlock) => {
    if (!currentNotebookId || !sectionTitle || editingConceptBlock === null) {
      return;
    }
    
    try {
      setSavingConceptBlock(true);
      await axios.put(
        `/notes/${currentNotebookId}/section/${encodeURIComponent(sectionTitle)}/concept-block/${editingConceptBlock}`,
        {
          conceptBlock: conceptBlock
        }
      );
      setEditingConceptBlock(null);
      setEditingConceptBlockData(null);
      if (onSectionUpdate) {
        onSectionUpdate();
      }
    } catch (error) {
      console.error('Error saving concept block:', error);
      alert('保存失败：' + (error.response?.data?.error || error.message));
    } finally {
      setSavingConceptBlock(false);
    }
  };


  return (
    <Box sx={{ mb: 4 }}>
      {/* Section 标题 */}
      {!hideTitle && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
              {order && `${order}. `}
              {title}
            </Typography>
          </Box>
          <Divider />
        </Box>
      )}

      {/* Introduction */}
      <IntroductionEditor
        introduction={introduction}
        isTeacher={isTeacher}
        notebookId={currentNotebookId}
        sectionTitle={sectionTitle}
        onUpdate={onSectionUpdate}
      />

      {/* Concept Blocks - 每个概念块作为一个整体显示 */}
      <Box sx={{ mb: 3 }}>
        {concept_blocks && concept_blocks.length > 0 && (
          <>
            {concept_blocks.map((block, blockIdx) => (
              <ConceptBlock
                key={blockIdx}
                block={block}
                blockIdx={blockIdx}
                isTeacher={isTeacher}
                notebookId={currentNotebookId}
                sectionTitle={sectionTitle}
                onUpdate={onSectionUpdate}
                onEdit={handleEditConceptBlock}
                onDelete={handleDeleteConceptBlock}
                deleting={deletingConceptBlock[blockIdx]}
              />
            ))}
            
            {/* 添加概念块按钮 */}
            {isTeacher && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                border: '2px dashed',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'grey.300',
                borderRadius: 3,
                textAlign: 'center',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'grey.400',
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'grey.100',
                },
              }}
            >
              <Button
                variant="outlined"
                startIcon={addingConceptBlock ? <CircularProgress size={16} /> : <AddIcon />}
                onClick={async () => {
                  if (!currentNotebookId || !sectionTitle || addingConceptBlock) {
                    return;
                  }
                  
                  try {
                    setAddingConceptBlock(true);
                    // 获取当前概念块数量
                    const currentBlockCount = concept_blocks ? concept_blocks.length : 0;
                    // 创建新的空概念块（通过设置一个空的定义）
                    await axios.put(
                      `/notes/${currentNotebookId}/section/${encodeURIComponent(sectionTitle)}/concept-block/${currentBlockCount}`,
                      {
                        field: 'definition',
                        value: ''
                      }
                    );
                    
                    // 刷新section数据
                    if (onSectionUpdate) {
                      onSectionUpdate();
                    }
                  } catch (error) {
                    console.error('Error adding concept block:', error);
                    alert('添加概念块失败：' + (error.response?.data?.error || error.message));
                  } finally {
                    setAddingConceptBlock(false);
                  }
                }}
                disabled={addingConceptBlock || !currentNotebookId || !sectionTitle}
                sx={{ 
                  textTransform: 'none',
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'grey.400',
                  color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : 'text.secondary',
                  '&:hover': {
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'grey.600',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'grey.200',
                  }
                }}
              >
                添加概念块
              </Button>
            </Paper>
            )}
          </>
        )}
        
        {/* 如果没有概念块，也显示添加按钮 */}
        {(!concept_blocks || concept_blocks.length === 0) && isTeacher && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              border: '2px dashed',
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'grey.300',
              borderRadius: 3,
              textAlign: 'center',
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'grey.400',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'grey.100',
              },
            }}
          >
            <Button
              variant="outlined"
              startIcon={addingConceptBlock ? <CircularProgress size={16} /> : <AddIcon />}
              onClick={async () => {
                if (!currentNotebookId || !sectionTitle || addingConceptBlock) {
                  return;
                }
                
                try {
                  setAddingConceptBlock(true);
                  // 创建第一个概念块（blockIndex = 0）
                  await axios.put(
                    `/notes/${currentNotebookId}/section/${encodeURIComponent(sectionTitle)}/concept-block/0`,
                    {
                      field: 'definition',
                      value: ''
                    }
                  );
                  
                  // 刷新section数据
                  if (onSectionUpdate) {
                    onSectionUpdate();
                  }
                } catch (error) {
                  console.error('Error adding concept block:', error);
                  alert('添加概念块失败：' + (error.response?.data?.error || error.message));
                } finally {
                  setAddingConceptBlock(false);
                }
              }}
              disabled={addingConceptBlock || !currentNotebookId || !sectionTitle}
              sx={{ 
                textTransform: 'none',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'grey.400',
                color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : 'text.secondary',
                '&:hover': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'grey.600',
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'grey.200',
                }
              }}
            >
              添加概念块
            </Button>
          </Paper>
        )}
      </Box>


      {/* Standalone Notes */}
      {standalone_notes && standalone_notes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 2, fontSize: '1.1rem', color: 'text.primary' }}>
            学习建议
          </Typography>
          {standalone_notes.map((note, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#000000',
                borderRadius: 2,
                borderLeft: '4px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'grey.400',
              }}
            >
              <Box sx={{ color: '#ffffff', lineHeight: 1.75 }}>
                <MarkdownRenderer content={note} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Exercises */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 3,
            backgroundColor: 'background.paper',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary'
              }}
            >
              <QuizIcon />
            </Box>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, fontSize: '1.1rem', color: 'text.primary' }}>
              练习题 {exercises && exercises.length > 0 && `(${exercises.length} 题)`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isTeacher && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setAddExerciseDialogOpen(true)}
                disabled={!currentNotebookId || !sectionTitle}
              >
                添加题目
              </Button>
            )}
            {exercises && exercises.length > 0 && (
              <Button
                variant="contained"
                startIcon={loadingFirstExercise ? <CircularProgress size={16} color="inherit" /> : <QuizIcon />}
                onClick={handleViewExercises}
                disabled={loadingFirstExercise || !currentNotebookId || !sectionTitle}
              >
                查看练习
              </Button>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Add Exercise Dialog */}
      {isTeacher && (
        <AddExerciseDialog
          open={addExerciseDialogOpen}
          onClose={() => setAddExerciseDialogOpen(false)}
          notebookId={currentNotebookId}
          sectionTitle={sectionTitle}
          onSuccess={() => {
            if (onSectionUpdate) {
              onSectionUpdate();
            }
          }}
        />
      )}

      {/* Summary */}
      <Summary 
        summary={summary}
        isTeacher={isTeacher}
        notebookId={currentNotebookId}
        sectionTitle={sectionTitle}
        onUpdate={onSectionUpdate}
      />

      <Divider sx={{ mt: 4 }} />

      {/* Edit Concept Block Dialog */}
      <EditConceptBlockDialog
        open={editingConceptBlock !== null}
        blockIndex={editingConceptBlock}
        conceptBlock={editingConceptBlockData}
        onClose={() => {
          setEditingConceptBlock(null);
          setEditingConceptBlockData(null);
        }}
        onSave={handleSaveConceptBlock}
        saving={savingConceptBlock}
      />
    </Box>
  );
};

export default Section;
